import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";
import { useDataStore } from "@/lib/store/data-store";
import type { Unit } from "@/lib/types/unit";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizeSearchText, getDescriptorSearchText } from "@/lib/utils/search-normalize";

interface UnitSelectorProps {
  selectedUnit: Unit | undefined;
  onUnitSelect: (unit: Unit | undefined) => void;
  placeholder?: string;
}

export function UnitSelector({
  selectedUnit,
  onUnitSelect,
  placeholder = "Search for unit",
}: UnitSelectorProps) {
  const { units, loadingUnits } = useDataStore();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Update query when selected unit changes
  useEffect(() => {
    if (selectedUnit) {
      setQuery(selectedUnit.name);
    }
  }, [selectedUnit]);

  // Filter units based on query
  const filteredUnits = useMemo(() => {
    if (!units || !query.trim()) return [];

    const normalizedQuery = normalizeSearchText(query);
    return units
      .filter((unit) => {
        const normalizedName = normalizeSearchText(unit.name);
        const normalizedDescriptor = normalizeSearchText(getDescriptorSearchText(unit.descriptorName));
        const nameMatch = normalizedName.includes(normalizedQuery);
        const descriptorMatch = normalizedDescriptor.includes(normalizedQuery);
        return nameMatch || descriptorMatch;
      })
      .slice(0, 10); // Limit to 10 results
  }, [units, query]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredUnits]);

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen, filteredUnits]);

  // Scroll selected item into view when navigating with keyboard
  useEffect(() => {
    const selectedElement = itemRefs.current.get(selectedIndex);
    if (selectedElement && dropdownRef.current) {
      selectedElement.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredUnits.length === 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredUnits.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredUnits[selectedIndex]) {
          handleResultClick(filteredUnits[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  const handleResultClick = (unit: Unit) => {
    onUnitSelect(unit);
    setIsOpen(false);
    setQuery(unit.name);
  };

  const handleClear = () => {
    onUnitSelect(undefined);
    setQuery("");
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.trim().length > 0 && filteredUnits.length > 0) {
              setIsOpen(true);
            }
          }}
          onBlur={(e) => {
            // Delay closing to allow click events
            // Capture the element reference before setTimeout (React pools events)
            const currentTarget = e.currentTarget;
            setTimeout(() => {
              // If currentTarget is null, the element was unmounted, so close the dropdown
              if (!currentTarget) {
                setIsOpen(false);
                return;
              }
              // Check if focus moved outside the input element
              if (!currentTarget.contains(document.activeElement)) {
                setIsOpen(false);
              }
            }, 200);
          }}
          className="w-full pr-10"
        />
        {loadingUnits && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {selectedUnit && query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            ×
          </button>
        )}
      </div>

      {isOpen &&
        filteredUnits.length > 0 &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[60] bg-popover text-popover-foreground rounded-md border shadow-md max-h-[300px] overflow-y-auto"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {filteredUnits.map((unit, index) => (
              <button
                key={unit.descriptorName}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(index, el);
                  } else {
                    itemRefs.current.delete(index);
                  }
                }}
                type="button"
                onClick={() => handleResultClick(unit)}
                className={cn(
                  "w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground transition-colors",
                  index === selectedIndex && "bg-accent text-accent-foreground"
                )}
              >
                <div className="font-medium">{unit.name}</div>
                <div className="text-xs text-muted-foreground">
                  {unit.descriptorName}
                </div>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

